import axios from 'axios';

export const addDealerBranding = (adminID, dealerID, brandingPayload, token) => {
  const resolvedDealerID = dealerID ?? brandingPayload?.dealerID ?? brandingPayload?.DealerID;

  return axios.post(
    `${process.env.REACT_APP_DEALER_API_GATEWAY_URL}addDealerBranding`,
    { adminID, ...brandingPayload, dealerID: resolvedDealerID },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        dealerID: resolvedDealerID,
      },
    }
  );
};

export const addDealerBrandings = addDealerBranding;

export const getDealerBrandingsByDealerID = (adminID, dealerID, token) => {
  return axios.post(
    `${process.env.REACT_APP_DEALER_API_GATEWAY_URL}getDealerBrandingsByDealerID`,
    { adminID, dealerID },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

const getUploadPayload = (response) => {
  const responseData = response?.data;
  if (responseData && typeof responseData === 'object' && responseData.data) {
    return responseData.data;
  }
  return responseData;
};

const isHttpUrl = (value) => /^https?:\/\//i.test(value);

const getDirectImageUrl = (payload) => {
  if (typeof payload === 'string' && payload.trim().length > 0) {
    return payload.trim();
  }

  if (!payload || typeof payload !== 'object') {
    return '';
  }

  const possibleUrl = payload.fileUrl
    || payload.url
    || payload.imageUrl
    || payload.uploadUrl
    || payload.location
    || payload.dealerBrandingImageUrl;

  return typeof possibleUrl === 'string' ? possibleUrl.trim() : '';
};

const getPreviewUrlFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return '';
  }

  const possiblePreviewUrl = payload.previewUrl
    || payload.imagePreviewUrl
    || payload.readUrl
    || payload.viewUrl
    || payload.downloadUrl
    || payload.signedGetUrl
    || payload.getSignedUrl
    || payload.fileUrl
    || payload.url
    || payload.imageUrl
    || payload.dealerBrandingImageUrl;

  return typeof possiblePreviewUrl === 'string' ? possiblePreviewUrl.trim() : '';
};

const encodeKeyForUrlPath = (key) => key
  .split('/')
  .filter((segment) => segment.length > 0)
  .map((segment) => encodeURIComponent(segment))
  .join('/');

const joinBaseAndKey = (baseUrl, key) => {
  const normalizedBaseUrl = typeof baseUrl === 'string' ? baseUrl.trim().replace(/\/+$/, '') : '';
  const normalizedKey = typeof key === 'string' ? key.trim().replace(/^\/+/, '') : '';
  if (!normalizedBaseUrl || !normalizedKey) {
    return '';
  }

  return `${normalizedBaseUrl}/${encodeKeyForUrlPath(normalizedKey)}`;
};

const parseUrlSafely = (value) => {
  try {
    return new URL(value);
  } catch (error) {
    return null;
  }
};

const extractS3KeyFromUrl = (value) => {
  const parsedUrl = parseUrlSafely(value);
  if (!parsedUrl) {
    return '';
  }

  const segments = decodeURIComponent(parsedUrl.pathname || '')
    .split('/')
    .filter(Boolean);

  if (!segments.length) {
    return '';
  }

  const host = (parsedUrl.hostname || '').toLowerCase();
  const isPathStyleS3Host = host === 's3.amazonaws.com'
    || host.startsWith('s3.')
    || host.startsWith('s3-');

  if (isPathStyleS3Host && segments.length > 1) {
    return segments.slice(1).join('/');
  }

  return segments.join('/');
};

const getImageKey = (payload) => {
  if (typeof payload === 'string') {
    const value = payload.trim();
    if (!value) {
      return '';
    }

    if (isHttpUrl(value)) {
      return extractS3KeyFromUrl(value);
    }

    return value;
  }

  if (!payload || typeof payload !== 'object') {
    return '';
  }

  const explicitKey = payload.key
    || payload.imageKey
    || payload.fileKey
    || payload.objectKey
    || payload.s3Key;

  if (typeof explicitKey === 'string' && explicitKey.trim()) {
    return explicitKey.trim();
  }

  const directImageUrl = getDirectImageUrl(payload);
  if (directImageUrl) {
    return extractS3KeyFromUrl(directImageUrl);
  }

  if (typeof payload.signedUrl === 'string' && payload.signedUrl.trim()) {
    return extractS3KeyFromUrl(payload.signedUrl);
  }

  return '';
};

const getDealerBrandingPreviewPayload = async (imageKey, dealerID, adminID, token) => {
  const response = await axios.post(
    `${process.env.REACT_APP_DEALER_API_GATEWAY_URL}getDealerBrandingUploadUrl`,
    {
      fileName: imageKey,
      fileType: 'image/*',
      dealerID,
      adminID,
      userID: adminID,
      imageKey,
      operation: 'view',
    },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  return getUploadPayload(response);
};

export const getDealerBrandingPreviewUrlFromKey = async (imageValue, dealerID, adminID, token) => {
  const normalizedValue = typeof imageValue === 'string' ? imageValue.trim() : '';
  if (!normalizedValue) {
    return '';
  }

  if (isHttpUrl(normalizedValue)) {
    return normalizedValue;
  }

  const envBaseUrl = process.env.REACT_APP_DEALER_BRANDING_IMAGE_BASE_URL;
  const fromEnvBaseUrl = joinBaseAndKey(envBaseUrl, normalizedValue);
  if (fromEnvBaseUrl) {
    return fromEnvBaseUrl;
  }

  if (!dealerID || !adminID || !token) {
    return '';
  }

  const payload = await getDealerBrandingPreviewPayload(normalizedValue, dealerID, adminID, token);
  const resolvedPreviewUrl = getPreviewUrlFromPayload(payload) || getDirectImageUrl(payload);

  return isHttpUrl(resolvedPreviewUrl) ? resolvedPreviewUrl : '';
};

export const getDealerBrandingUploadUrl = async (file, dealerID, adminID, token) => {
  if (!file || !dealerID || !adminID || !token) {
    throw new Error('Missing required data for branding image upload.');
  }

  const response = await axios.post(
    `${process.env.REACT_APP_DEALER_API_GATEWAY_URL}getDealerBrandingUploadUrl`,
    {
      fileName: file.name,
      fileType: file.type,
      dealerID,
      adminID,
      userID: adminID,
    },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  const payload = getUploadPayload(response);
  const signedUrl = payload?.signedUrl;
  const previewUrlCandidate = getPreviewUrlFromPayload(payload) || getDirectImageUrl(payload);
  let imageKey = getImageKey(payload);

  if (typeof signedUrl === 'string' && signedUrl.length > 0) {
    const uploadResult = await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers: file.type ? { 'Content-Type': file.type } : undefined,
    });

    if (!uploadResult.ok) {
      throw new Error('Failed to upload image to storage.');
    }

    if (!imageKey) {
      imageKey = extractS3KeyFromUrl(signedUrl);
    }
  }

  if (!imageKey) {
    throw new Error('Image key was not returned from upload API.');
  }

  return { imageKey, imageUrl: isHttpUrl(previewUrlCandidate) ? previewUrlCandidate : '' };
};
